import React from 'react';
import { ArrowLeft, X, Save } from 'lucide-react';

interface FocusHeaderProps {
  title: string;
  subtitle?: string;
  // Terugpijl links. Weglaten = geen pijl (de titel blijft gecentreerd).
  onBack?: () => void;
  backLabel?: string;
  // Sluiten-knop rechts (wizard: naar het conceptenoverzicht).
  onClose?: () => void;
  closeLabel?: string;
  // Geen Opgeslagen-indicator tonen (schermen zonder iets om op te slaan).
  hideSaveStatus?: boolean;
  onCancel?: () => void;
  onSave?: () => void;
  isSaving?: boolean;
  saveDisabled?: boolean;
  hasUnsavedChanges?: boolean;
  saveButtonText?: string;
  customActions?: React.ReactNode;
}

export function FocusHeader({
  title,
  subtitle,
  onBack,
  backLabel = 'Terug',
  onClose,
  closeLabel = 'Sluiten',
  hideSaveStatus = false,
  onCancel,
  onSave,
  isSaving = false,
  saveDisabled = false,
  hasUnsavedChanges = false,
  saveButtonText = 'Opslaan',
  customActions,
}: FocusHeaderProps) {
  return (
    <div className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              title={backLabel}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          ) : (
            <div className="w-5" aria-hidden="true" />
          )}

          <div className="text-center px-4 min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-gray-600 truncate">{subtitle}</p>
            )}
          </div>

          <div className="flex items-center gap-3">
            {customActions}

            {!customActions && (
              <>
                {!hideSaveStatus && (
                  <div className="hidden sm:flex items-center gap-2 text-sm min-w-[120px]">
                    {hasUnsavedChanges ? (
                      <>
                        <div className="w-2 h-2 bg-amber-600 rounded-full" />
                        <span className="text-amber-600">Niet opgeslagen</span>
                      </>
                    ) : (
                      <>
                        <div className="w-2 h-2 bg-green-600 rounded-full" />
                        <span className="text-green-600">Opgeslagen</span>
                      </>
                    )}
                  </div>
                )}

                {onCancel && (
                  <button
                    type="button"
                    onClick={onCancel}
                    className="btn-secondary inline-flex items-center gap-2 px-3 py-2 text-sm"
                  >
                    <X className="w-4 h-4" />
                    <span className="hidden sm:inline">Annuleer</span>
                  </button>
                )}

                {onSave && (
                  <button
                    type="button"
                    onClick={onSave}
                    disabled={isSaving || saveDisabled}
                    className="btn-primary inline-flex items-center gap-2 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4" />
                    <span className="hidden sm:inline">{isSaving ? 'Opslaan...' : saveButtonText}</span>
                  </button>
                )}
              </>
            )}

            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary inline-flex items-center gap-2 px-3 py-2 text-sm"
                aria-label={closeLabel}
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">{closeLabel}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
